import React from "react";
import { cx } from "components/Graphs/utils";

type Alignment = "left" | "right";

export interface NodeDetailSectionProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  "title"
> {
  title?: React.ReactNode;
}

export const NodeDetailSection = React.forwardRef<
  HTMLElement,
  NodeDetailSectionProps
>(({ className, title, children, ...props }, ref) => (
  <section
    ref={ref}
    className={cx(
      "flex w-full flex-col items-stretch justify-start",
      title != null && "mt-12",
      className,
    )}
    {...props}
  >
    {title != null && (
      <>
        <h1 className="text-lg font-semibold text-white">{title}</h1>
        <hr className="my-3 w-full dark:border dark:border-zinc-800" />
      </>
    )}
    {children}
  </section>
));

NodeDetailSection.displayName = "NodeDetailSection";

export interface NodeDetailSectionHeaderProps {
  children?: React.ReactNode;
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  className?: string;
}

export function NodeDetailSectionHeader({
  children,
  leading,
  trailing,
  className,
}: NodeDetailSectionHeaderProps) {
  const leadingNode = children ?? leading;
  const alignEnd = !leadingNode && Boolean(trailing);

  return (
    <div
      className={cx(
        "mb-4 flex w-full items-center gap-4",
        alignEnd ? "justify-end" : "justify-between",
        className,
      )}
    >
      {leadingNode ? <div className="min-w-0">{leadingNode}</div> : null}
      {trailing ? <div className="shrink-0">{trailing}</div> : null}
    </div>
  );
}

export function NodeDetailSectionDescription({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return <p className={cx("text-sm text-secondary", className)}>{children}</p>;
}

export type NodeDetailTableProps = React.TableHTMLAttributes<HTMLTableElement>;

export function NodeDetailTable({
  className,
  children,
  ...props
}: NodeDetailTableProps) {
  return (
    <table
      className={cx(
        "w-full divide-y divide-gray-300 overflow-hidden rounded-sm bg-secondaryBg dark:divide-zinc-800",
        className,
      )}
      {...props}
    >
      {children}
    </table>
  );
}

export type NodeDetailTableHeadProps =
  React.HTMLAttributes<HTMLTableSectionElement>;

export function NodeDetailTableHead({
  className,
  ...props
}: NodeDetailTableHeadProps) {
  return (
    <thead
      className={cx("bg-gray-50 dark:bg-zinc-800", className)}
      {...props}
    />
  );
}

export interface NodeDetailTableHeaderCellProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  align?: Alignment;
}

export function NodeDetailTableHeaderCell({
  align = "left",
  className,
  ...props
}: NodeDetailTableHeaderCellProps) {
  return (
    <th
      scope="col"
      className={cx(
        "px-3 py-3.5 text-sm font-semibold text-gray-900 dark:text-white",
        align === "right" ? "text-right" : "text-left",
        className,
      )}
      {...props}
    />
  );
}

export type NodeDetailTableBodyProps =
  React.HTMLAttributes<HTMLTableSectionElement>;

export function NodeDetailTableBody({
  className,
  ...props
}: NodeDetailTableBodyProps) {
  return (
    <tbody
      className={cx("divide-y divide-gray-200 bg-secondaryBg", className)}
      {...props}
    />
  );
}

export interface NodeDetailTableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  align?: Alignment;
}

export function NodeDetailTableCell({
  align = "left",
  className,
  ...props
}: NodeDetailTableCellProps) {
  return (
    <td
      className={cx(
        "px-3 py-3 text-sm",
        align === "right" ? "text-right" : "text-left",
        className,
      )}
      {...props}
    />
  );
}

export type NodeDetailIconActionProps =
  React.ButtonHTMLAttributes<HTMLButtonElement>;

export const NodeDetailIconAction = React.forwardRef<
  HTMLButtonElement,
  NodeDetailIconActionProps
>(({ className, type = "button", ...props }, ref) => (
  <button
    ref={ref}
    type={type}
    className={cx(
      "rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-highlight dark:hover:bg-zinc-800",
      className,
    )}
    {...props}
  />
));

NodeDetailIconAction.displayName = "NodeDetailIconAction";
